import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Siren, ShieldCheck, Radio, AlertTriangle, ListChecks, Loader2, RefreshCw,
  ExternalLink, Phone, MapPin, CheckCircle2, RotateCcw, CircleDot, XCircle, Flag,
} from "lucide-react";
import { adminApi, formatApiError } from "./api";
import { PageHeader } from "./shared";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { hour: "numeric", minute: "2-digit" }) : "—");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const fmtAgo = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric" });
};

const PRIORITY_STYLE = {
  high: "bg-[#e4002b]/10 text-[#e4002b]",
  medium: "bg-[#f59e0b]/12 text-[#b45309]",
  low: "bg-[#16a34a]/10 text-[#16a34a]",
};

const FEED_ICON = {
  emergency: { icon: Siren, color: "text-[#e4002b]", bg: "bg-[#e4002b]/10" },
  report: { icon: Flag, color: "text-[#2340b5]", bg: "bg-[#2340b5]/10" },
  share_started: { icon: CircleDot, color: "text-[#16a34a]", bg: "bg-[#16a34a]/10" },
  share_stopped: { icon: XCircle, color: "text-[#565b73]", bg: "bg-[#565b73]/10" },
  trip_completed: { icon: CheckCircle2, color: "text-[#16a34a]", bg: "bg-[#16a34a]/10" },
};

// Track links point at our own branded public tracking page (the upstream
// maple-rides /track/{token} page is not served — the data API is).
const localTrackUrl = (url) => {
  const token = (url || "").split("/track/")[1];
  return token ? `/track/${token}` : url;
};

function AlertDialog({ alert, reasons, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  if (!alert) return null;
  const resolve = async () => {
    setBusy(true);
    try {
      await adminApi.patch(`/safety/alerts/${alert.id}`, { status: "resolved" });
      toast.success(`Alert for ${alert.user_name} marked resolved`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update the alert");
    } finally {
      setBusy(false);
    }
  };
  const row = (label, value) => (
    <div className="flex items-start justify-between gap-4 border-b border-[#12142b]/6 py-2.5 text-sm last:border-0">
      <span className="text-[#565b73]">{label}</span>
      <span className="text-right font-semibold text-[#12142b]">{value}</span>
    </div>
  );
  return (
    <Dialog open={!!alert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="safety-alert-dialog">
        <DialogHeader><DialogTitle>Emergency alert</DialogTitle>
          <DialogDescription className="sr-only">Emergency alert details and resolution action</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3 rounded-xl bg-[#e4002b]/6 p-3">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-[#e4002b]/12"><Siren className="h-5 w-5 text-[#e4002b]" /></span>
          <div>
            <p className="font-display text-base font-bold text-[#12142b]">{alert.user_name} <span className="text-xs font-semibold text-[#565b73]">· {alert.role}</span></p>
            <p className="text-xs text-[#565b73]">{fmtDateTime(alert.time)}</p>
          </div>
          <span className={`ml-auto rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${alert.status === "active" ? "bg-[#e4002b]/10 text-[#e4002b]" : "bg-[#16a34a]/10 text-[#16a34a]"}`}>{alert.status}</span>
        </div>
        <div className="mt-2">
          {row("Trip", alert.route)}
          {row("Emergency contact", alert.contact || (alert.contacts_notified ? `${alert.contacts_notified} notified` : "Not notified"))}
          {row("Location", alert.location?.lat ? `${alert.location.lat.toFixed(4)}, ${alert.location.lng.toFixed(4)}` : "Not shared")}
          {alert.track_url && (
            <a href={localTrackUrl(alert.track_url)} target="_blank" rel="noreferrer" data-testid="safety-alert-track"
              className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-[#2340b5]/8 px-3 py-2 text-xs font-bold text-[#2340b5] hover:bg-[#2340b5]/14">
              <MapPin className="h-3.5 w-3.5" /> Open live tracking <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <DialogFooter className="mt-2 gap-2 sm:justify-between">
          <button data-testid="safety-alert-close" onClick={onClose}
            className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb]">Close</button>
          {alert.status === "active" && (
            <button data-testid="safety-alert-resolve" onClick={resolve} disabled={busy}
              className="inline-flex items-center gap-2 rounded-xl bg-[#16a34a] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#15803d] disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Mark resolved
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog({ report, onClose, onDone }) {
  const [busy, setBusy] = useState("");
  if (!report) return null;
  const setStatus = async (status) => {
    setBusy(status);
    try {
      await adminApi.patch(`/safety/reports/${report.id}`, { status });
      toast.success(status === "resolved" ? "Report marked resolved" : "Report reopened");
      onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update the report");
    } finally {
      setBusy("");
    }
  };
  return (
    <Dialog open={!!report} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="safety-report-dialog">
        <DialogHeader><DialogTitle>Review report {report.id?.slice(0, 8).toUpperCase()}</DialogTitle>
          <DialogDescription className="sr-only">Safety report details, reason and resolution action</DialogDescription>
        </DialogHeader>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between border-b border-[#12142b]/6 py-2.5"><span className="text-[#565b73]">Reported user</span><span className="font-semibold text-[#12142b]">{report.reported_user} <span className="text-xs font-semibold text-[#565b73]">· {report.reported_role}</span></span></div>
          <div className="flex justify-between border-b border-[#12142b]/6 py-2.5"><span className="text-[#565b73]">Reported by</span><span className="font-semibold text-[#12142b]">{report.reported_by} <span className="text-xs font-semibold text-[#565b73]">· {report.reported_by_role}</span></span></div>
          <div className="flex justify-between border-b border-[#12142b]/6 py-2.5"><span className="text-[#565b73]">Trip</span><span className="font-semibold text-[#12142b]">{report.route}</span></div>
          <div className="flex justify-between border-b border-[#12142b]/6 py-2.5"><span className="text-[#565b73]">Submitted</span><span className="font-semibold text-[#12142b]">{fmtDateTime(report.submitted)}</span></div>
          <div className="flex justify-between py-2.5"><span className="text-[#565b73]">Status</span>
            <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${report.status === "open" ? "bg-[#2340b5]/10 text-[#2340b5]" : "bg-[#16a34a]/10 text-[#16a34a]"}`}>{report.status}</span>
          </div>
        </div>
        <div className="mt-1">
          <label className="mb-1.5 block text-sm font-semibold text-[#12142b]">Reason</label>
          <div className="flex items-center justify-between rounded-xl bg-[#f4f6fb] px-3 py-2.5 text-sm" data-testid="safety-report-reason">
            <span className={report.reason ? "font-semibold text-[#12142b]" : "italic text-[#565b73]"}>{report.reason || "Not specified"}</span>
            {report.priority && (
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold capitalize ${PRIORITY_STYLE[report.priority]}`}>{report.priority}</span>
            )}
          </div>
          <p className="mt-1.5 text-[11px] text-[#565b73]/70">Exactly as the reporter selected it in the app — review only, it can't be edited.</p>
        </div>
        <DialogFooter className="mt-2 gap-2 sm:justify-between">
          <button data-testid="safety-report-close" onClick={onClose}
            className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb]">Close</button>
          {report.status === "open" ? (
            <button data-testid="safety-report-resolve" onClick={() => setStatus("resolved")} disabled={!!busy}
              className="inline-flex items-center gap-2 rounded-xl bg-[#16a34a] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#15803d] disabled:opacity-60">
              {busy === "resolved" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Mark resolved
            </button>
          ) : (
            <button data-testid="safety-report-reopen" onClick={() => setStatus("open")} disabled={!!busy}
              className="inline-flex items-center gap-2 rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1b3390] disabled:opacity-60">
              {busy === "open" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Reopen
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SafetyCenter() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("all");
  const [viewAlert, setViewAlert] = useState(null);
  const [viewReport, setViewReport] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep links: /admin/m/safety-center?report=<id> or ?alert=<id> open the
  // matching dialog straight from other sections (e.g. finance "View Dispute").
  useEffect(() => {
    if (!data) return;
    const rid = searchParams.get("report");
    const aid = searchParams.get("alert");
    if (rid) {
      const match = (data.reports || []).find((r) => r.id === rid);
      if (match) setViewReport(match);
      else toast.error("That safety report was not found");
      setSearchParams({}, { replace: true });
    } else if (aid) {
      const match = (data.alerts || []).find((a) => a.id === aid);
      if (match) setViewAlert(match);
      else toast.error("That emergency alert was not found");
      setSearchParams({}, { replace: true });
    }
  }, [data, searchParams, setSearchParams]);

  const load = useCallback((silent = false) => {
    adminApi.get("/safety/overview")
      .then((r) => { setData(r.data); setError(""); })
      .catch((e) => {
        if (!silent) {
          setError(formatApiError(e.response?.data?.detail) || "Could not load safety data");
          setData(null);
        }
      });
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(() => load(true), 15000); // live feed refresh
    return () => clearInterval(id);
  }, [load]);

  const alerts = (data?.alerts || []).filter((a) => tab === "all" || a.status === tab);
  const shares = data?.live_shares || [];
  const reports = data?.reports || [];
  const feed = data?.feed || [];
  const counts = data?.counts || { active_alerts: 0, live_shares: 0, open_reports: 0 };

  const cardCls = "rounded-2xl border border-[#12142b]/8 bg-white shadow-sm";
  const thCls = "pb-3 font-semibold";
  const headRow = "border-b border-[#12142b]/8 text-left text-xs uppercase tracking-wide text-[#565b73]";

  return (
    <div data-testid="safety-page">
      <PageHeader title="Safety Center" subtitle="Emergency alerts, live location shares and safety reports across live trips."
        right={<button data-testid="safety-refresh" onClick={() => load()}
          className="inline-flex items-center gap-2 rounded-xl border border-[#12142b]/12 bg-white px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb]">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>} />

      <div className="space-y-6 p-8">
        {error && (
          <div className="rounded-2xl border border-[#e4002b]/20 bg-[#e4002b]/5 p-5 text-center" data-testid="safety-error">
            <p className="text-sm font-semibold text-[#e4002b]">{error}</p>
            <button data-testid="safety-retry" onClick={() => load()}
              className="mt-3 rounded-xl bg-[#e4002b] px-4 py-2 text-sm font-bold text-white hover:bg-[#c40024]">Retry</button>
          </div>
        )}
        {data === null && !error && <p className="py-10 text-center text-sm text-[#565b73]">Loading safety data…</p>}

        {data && (
          <>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              {/* Emergency Alert Log */}
              <section className={cardCls} data-testid="safety-alerts-card">
                <div className="flex items-center justify-between border-b border-[#12142b]/8 px-6 py-4">
                  <h2 className="flex items-center gap-2 font-display text-lg font-bold text-[#12142b]">
                    <Siren className="h-5 w-5 text-[#e4002b]" strokeWidth={1.75} /> Emergency Alert Log
                  </h2>
                  <div className="flex rounded-full bg-[#f4f6fb] p-1" data-testid="safety-alert-tabs">
                    {[["all", "All"], ["active", "Active"], ["resolved", "Resolved"]].map(([k, label]) => (
                      <button key={k} data-testid={`safety-tab-${k}`} onClick={() => setTab(k)}
                        className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${tab === k ? "bg-[#12142b] text-white" : "text-[#565b73] hover:text-[#12142b]"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="overflow-x-auto p-6 pt-3">
                  {alerts.length === 0 ? (
                    <p className="py-8 text-center text-sm text-[#565b73]" data-testid="safety-alerts-empty">No emergency alerts{tab !== "all" ? ` (${tab})` : ""}.</p>
                  ) : (
                    <table className="w-full text-left text-sm">
                      <thead><tr className={headRow}>
                        <th className={thCls}>Time</th><th className={thCls}>User</th><th className={thCls}>Trip</th>
                        <th className={thCls}>Emergency Contact</th><th className={thCls}>Status</th><th className={thCls}>Action</th>
                      </tr></thead>
                      <tbody>
                        {alerts.map((a) => (
                          <tr key={a.id} data-testid={`safety-alert-row-${a.id.slice(0, 8)}`} className="border-b border-[#12142b]/5 last:border-0">
                            <td className="py-3.5 text-[#565b73]">{fmtTime(a.time)}</td>
                            <td className="py-3.5">
                              <p className="font-semibold text-[#12142b]">{a.user_name}</p>
                              <p className="text-xs text-[#565b73]">{a.role}</p>
                            </td>
                            <td className="py-3.5 text-[#565b73]">{a.route}</td>
                            <td className="py-3.5">
                              {a.contact ? (
                                <span className="inline-flex items-center gap-1.5 text-[#12142b]"><Phone className="h-3.5 w-3.5 text-[#565b73]" />{a.contact}</span>
                              ) : (
                                <span className="text-xs text-[#565b73]">{a.contacts_notified ? `${a.contacts_notified} notified` : "—"}</span>
                              )}
                            </td>
                            <td className="py-3.5">
                              <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${a.status === "active" ? "bg-[#e4002b]/10 text-[#e4002b]" : "bg-[#16a34a]/10 text-[#16a34a]"}`}>{a.status}</span>
                            </td>
                            <td className="py-3.5">
                              <button data-testid={`safety-view-alert-${a.id.slice(0, 8)}`} onClick={() => setViewAlert(a)}
                                className="rounded-lg bg-[#12142b] px-3.5 py-1.5 text-xs font-bold text-white hover:bg-[#2340b5]">View alert</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </section>

              {/* Live Trips Shared with Safety Team */}
              <section className={cardCls} data-testid="safety-live-card">
                <div className="flex items-center justify-between border-b border-[#12142b]/8 px-6 py-4">
                  <h2 className="flex items-center gap-2 font-display text-lg font-bold text-[#12142b]">
                    <Radio className="h-5 w-5 text-[#2340b5]" strokeWidth={1.75} /> Live Trips Shared with Safety Team
                  </h2>
                  <span data-testid="safety-live-count" className="rounded-full bg-[#16a34a]/10 px-3 py-1 text-xs font-bold text-[#16a34a]">
                    {counts.live_shares} Live Trip{counts.live_shares === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="overflow-x-auto p-6 pt-3">
                  {shares.length === 0 ? (
                    <p className="py-8 text-center text-sm text-[#565b73]" data-testid="safety-live-empty">No trips are sharing their location right now.</p>
                  ) : (
                    <table className="w-full text-left text-sm">
                      <thead><tr className={headRow}>
                        <th className={thCls}>User</th><th className={thCls}>Route</th><th className={thCls}>Last Update</th><th className={thCls}>Track</th>
                      </tr></thead>
                      <tbody>
                        {shares.map((s) => (
                          <tr key={s.id} data-testid={`safety-live-row-${s.id.slice(0, 8)}`} className="border-b border-[#12142b]/5 last:border-0">
                            <td className="py-3.5">
                              <span className="mr-2 inline-flex items-center rounded-full bg-[#e4002b]/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-[#e4002b]">Live</span>
                              <span className="font-semibold text-[#12142b]">{s.user}</span>
                            </td>
                            <td className="py-3.5 text-[#565b73]">{s.route}</td>
                            <td className="py-3.5 text-[#565b73]">{fmtAgo(s.last_update)}</td>
                            <td className="py-3.5">
                              <a href={localTrackUrl(s.track_url)} target="_blank" rel="noreferrer" data-testid={`safety-track-${s.id.slice(0, 8)}`}
                                className="inline-flex items-center gap-1 text-xs font-bold text-[#2340b5] hover:underline">
                                Track <ExternalLink className="h-3 w-3" />
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </section>
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[3fr_2fr]">
              {/* Dangerous Driver / Rider Reports */}
              <section className={cardCls} data-testid="safety-reports-card">
                <div className="flex items-center justify-between border-b border-[#12142b]/8 px-6 py-4">
                  <h2 className="flex items-center gap-2 font-display text-lg font-bold text-[#12142b]">
                    <ShieldCheck className="h-5 w-5 text-[#2340b5]" strokeWidth={1.75} /> Dangerous Driver / Rider Reports
                  </h2>
                  {counts.open_reports > 0 && (
                    <span data-testid="safety-reports-count" className="rounded-full bg-[#e4002b]/10 px-3 py-1 text-xs font-bold text-[#e4002b]">
                      {counts.open_reports} Open Report{counts.open_reports === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
                <div className="overflow-x-auto p-6 pt-3">
                  {reports.length === 0 ? (
                    <p className="py-8 text-center text-sm text-[#565b73]" data-testid="safety-reports-empty">No safety reports submitted.</p>
                  ) : (
                    <table className="w-full text-left text-sm">
                      <thead><tr className={headRow}>
                        <th className={thCls}>Report ID</th><th className={thCls}>Reported User</th><th className={thCls}>Reported By</th>
                        <th className={thCls}>Reason</th><th className={thCls}>Submitted</th><th className={thCls}>Priority</th><th className={thCls}>Action</th>
                      </tr></thead>
                      <tbody>
                        {reports.map((r) => (
                          <tr key={r.id} data-testid={`safety-report-row-${r.id.slice(0, 8)}`} className="border-b border-[#12142b]/5 last:border-0">
                            <td className="py-3.5 font-mono text-xs text-[#565b73]">RPT-{r.id.slice(0, 4).toUpperCase()}</td>
                            <td className="py-3.5">
                              <p className="font-semibold text-[#12142b]">{r.reported_user}</p>
                              <p className="text-xs text-[#565b73]">{r.reported_role}</p>
                            </td>
                            <td className="py-3.5">
                              <p className="font-semibold text-[#12142b]">{r.reported_by}</p>
                              <p className="text-xs text-[#565b73]">{r.reported_by_role}</p>
                            </td>
                            <td className="py-3.5 text-[#565b73]">{r.reason || <span className="text-xs italic">Uncategorized</span>}</td>
                            <td className="py-3.5 text-[#565b73]">{fmtDateTime(r.submitted)}</td>
                            <td className="py-3.5">
                              {r.priority ? (
                                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold capitalize ${PRIORITY_STYLE[r.priority]}`}>{r.priority}</span>
                              ) : <span className="text-xs text-[#565b73]">—</span>}
                            </td>
                            <td className="py-3.5">
                              <button data-testid={`safety-review-report-${r.id.slice(0, 8)}`} onClick={() => setViewReport(r)}
                                className="rounded-lg bg-[#2340b5]/10 px-3.5 py-1.5 text-xs font-bold text-[#2340b5] hover:bg-[#2340b5]/20">Review report</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </section>

              {/* Safety Activity Feed */}
              <section className={cardCls} data-testid="safety-feed-card">
                <div className="flex items-center justify-between border-b border-[#12142b]/8 px-6 py-4">
                  <h2 className="flex items-center gap-2 font-display text-lg font-bold text-[#12142b]">
                    <ListChecks className="h-5 w-5 text-[#2340b5]" strokeWidth={1.75} /> Safety Activity Feed
                  </h2>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#565b73]">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-[#16a34a]" /> live
                  </span>
                </div>
                <div className="max-h-[520px] overflow-y-auto p-6 pt-3">
                  {feed.length === 0 ? (
                    <p className="py-8 text-center text-sm text-[#565b73]" data-testid="safety-feed-empty">No safety activity yet.</p>
                  ) : (
                    <ul className="divide-y divide-[#12142b]/5">
                      {feed.map((f) => {
                        const style = FEED_ICON[f.kind] || FEED_ICON.report;
                        const Icon = style.icon;
                        return (
                          <li key={f.id} data-testid={`safety-feed-${f.id}`} className="flex items-start gap-3 py-3.5">
                            <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${style.bg}`}>
                              <Icon className={`h-4 w-4 ${style.color}`} />
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold leading-snug text-[#12142b]">{f.text}</p>
                            </div>
                            <span className="shrink-0 text-xs text-[#565b73]">{fmtTime(f.at)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </section>
            </div>

            {reports.length === 0 && alerts.length === 0 && (
              <p className="text-center text-xs text-[#565b73]/70 flex items-center justify-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> Trip-completed and share-stopped events appear here as the live API records them.
              </p>
            )}
          </>
        )}
      </div>

      <AlertDialog alert={viewAlert} onClose={() => setViewAlert(null)} onDone={() => load(true)} />
      <ReportDialog report={viewReport} onClose={() => setViewReport(null)} onDone={() => load(true)} />
    </div>
  );
}
